import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import api from '../api/client';

interface Application {
  id: number;
  name: string;
  phone: string;
  telegram_id: number | null;
  web_username: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string;
  approved_at: string | null;
  shop: number | null;
  created_at: string;
}

type Filter = 'pending' | 'approved' | 'rejected' | '';

const STATUS_META: Record<string, { label: string; cls: string; Icon: React.FC<any> }> = {
  pending: { label: "Ko'rib chiqilmoqda", cls: 'bg-amber-100 text-amber-700', Icon: Clock },
  approved: { label: 'Tasdiqlangan', cls: 'bg-success-100 text-success-700', Icon: CheckCircle },
  rejected: { label: 'Rad etilgan', cls: 'bg-red-100 text-red-700', Icon: XCircle },
};

export const Applications: React.FC = () => {
  const [items, setItems] = useState<Application[]>([]);
  const [filter, setFilter] = useState<Filter>('pending');
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState<Application | null>(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/applications/', { params: filter ? { status: filter } : {} });
      setItems(res.data.results ?? res.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (app: Application) => {
    setBusyId(app.id);
    try {
      await api.post(`/applications/${app.id}/approve/`);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.id);
    try {
      await api.post(`/applications/${rejecting.id}/reject/`, { reason });
      setRejecting(null);
      setReason('');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sotuvchi arizalari</h1>
        <button onClick={load} className="btn btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Yangilash
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {([
          ['pending', "Ko'rib chiqilmoqda"],
          ['approved', 'Tasdiqlangan'],
          ['rejected', 'Rad etilgan'],
          ['', 'Hammasi'],
        ] as [Filter, string][]).map(([val, label]) => (
          <button
            key={val || 'all'}
            onClick={() => setFilter(val)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              filter === val ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Yuklanmoqda...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Ariza yo'q</div>
      ) : (
        <div className="space-y-3">
          {items.map((app) => {
            const meta = STATUS_META[app.status];
            const Icon = meta.Icon;
            return (
              <div key={app.id} className="card flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{app.name}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {app.phone} · login: {app.web_username || '—'}
                    {app.telegram_id ? ` · TG: ${app.telegram_id}` : ''}
                  </div>
                  {app.status === 'rejected' && app.rejection_reason && (
                    <div className="text-sm text-red-600 mt-1">Sabab: {app.rejection_reason}</div>
                  )}
                  {app.status === 'approved' && (
                    <div className="text-sm text-gray-500 mt-1">
                      Do'kon: {app.shop ? 'yaratilgan' : 'hali yo\'q'}
                    </div>
                  )}
                </div>

                {app.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(app)}
                      disabled={busyId === app.id}
                      className="btn btn-primary flex items-center gap-1"
                    >
                      <Check className="w-4 h-4" /> Tasdiqlash
                    </button>
                    <button
                      onClick={() => { setRejecting(app); setReason(''); }}
                      disabled={busyId === app.id}
                      className="btn btn-danger flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Rad etish
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Arizani rad etish</h2>
            <p className="text-sm text-gray-600 mb-4">{rejecting.name} — sababni yozing (sotuvchi ko'radi):</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="input mb-4"
              placeholder="Masalan: telefon raqam noto'g'ri"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejecting(null)} className="btn btn-secondary">Bekor</button>
              <button onClick={submitReject} disabled={busyId === rejecting.id} className="btn btn-danger">
                Rad etish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
